/**
 * How far a document has got through classification, which is what decides whether its
 * contents can be answered from.
 *
 * A document is uploaded, classified, and only then indexed - so between the upload and the
 * end of classification it is present, openable, correctly named, and not answerable. Nothing
 * about it looks unfinished, which is the whole problem: a user who asks the assistant about a
 * file they just uploaded used to be told it did not exist (#4637), and a user who never asks
 * is told nothing at all (#4788).
 *
 * The values are numbers because that is what the API sends: it serializes with
 * Newtonsoft.Json and registers no string-enum converter, so every enum it returns is an
 * integer. `Pending` is 0, so beware `status || fallback` and `if (status)` - a document that
 * is still processing is falsy. Use the predicates below, or `??`.
 *
 * `undefined` is a fourth state and deliberately not folded into any of these. It means the
 * payload did not say - an older API, a projection that omits it - and the honest rendering of
 * "did not say" is to show nothing rather than to guess at either end.
 */
export enum DocumentClassificationStatus {
  /** Uploaded, not yet classified. Usually under a minute; not answerable meanwhile. */
  Pending = 0,
  /** Classified. The document is answerable, subject to the reader's content grants. */
  Succeeded = 1,
  /**
   * Classification ran and failed. Waiting will not change it, and the content is withheld.
   *
   * Its own state rather than a variety of `Pending`, because the advice is the opposite one:
   * a pending document arrives on its own and a failed one never does, so telling someone to
   * wait for it would be worse than saying nothing. The assistant draws the same distinction
   * for the same reason - see `WithheldDocumentReason.ClassificationFailed`.
   *
   * The classifier could not say what is in the file, so the file stays withheld: opening or
   * downloading it answers 403 until it is processed again or an administrator overrides the
   * classification. That is the one state where the reader cannot reach their own upload.
   */
  Failed = 2,

  /**
   * The classifier read the file and found no text in it. Usually a scan or an image with no
   * text layer.
   *
   * Distinct from `Failed`, and the distinction is the difference between two opposite pieces
   * of advice. Nothing went wrong: the document opens and downloads exactly as it always did,
   * and only the assistant is affected by it. Re-uploading the same file reaches the same
   * result, so the one thing the `Failed` copy suggests is the one thing that cannot work here.
   * See `DocumentClassificationStatus.Unindexable` on the API side.
   */
  Unindexable = 3,
}

/**
 * The document is still being processed, so its contents are not answerable yet.
 *
 * False for `undefined`: an absent status is not evidence of processing.
 */
export function isBeingProcessed(status: DocumentClassificationStatus | undefined): boolean {
  return status === DocumentClassificationStatus.Pending;
}

/** Classification finished successfully, so the document can be answered from. */
export function isProcessed(status: DocumentClassificationStatus | undefined): boolean {
  return status === DocumentClassificationStatus.Succeeded;
}

/** Classification finished and failed. The document will not become answerable on its own. */
export function processingFailed(status: DocumentClassificationStatus | undefined): boolean {
  return status === DocumentClassificationStatus.Failed;
}

/**
 * The file holds no text, so it will never be answerable and nothing is wrong with it.
 *
 * Kept apart from `processingFailed` everywhere, because the two look alike and read
 * oppositely: one is a document the reader cannot open, the other is a document that opens
 * normally and simply has nothing in it to search.
 */
export function hasNoExtractableText(
  status: DocumentClassificationStatus | undefined,
): boolean {
  return status === DocumentClassificationStatus.Unindexable;
}

/**
 * Whether this document's state is worth telling the reader about.
 *
 * A processed document needs no annotation - it is the state every document is expected to be
 * in - and an absent status has nothing to say. Only the three that explain why a document is
 * not answerable are worth the space.
 */
export function needsExplaining(status: DocumentClassificationStatus | undefined): boolean {
  return isBeingProcessed(status) || processingFailed(status) || hasNoExtractableText(status);
}
